import { useCallback } from 'react'

import { useAuth } from 'contexts/auth/AuthContext'
import { useOrg } from 'contexts/OrgContext'

import { JsonDataZodSchema } from './validateApiData'

export type RequestOptions<TResponseSchema extends JsonDataZodSchema | undefined = undefined> =
  RequestInit & {
    /**
     * Any query params for the request. These will be serialized
     * using the "qs" library and appended to the API url.
     */
    params?: unknown
    /**
     * Any data to be sent in the request body. This will be serialized as JSON
     * prior to sending using `JSON.stringify`.
     */
    data?: unknown
    /**
     * A zod schema representing the data that will be returned back from this API call. If given, the
     * incoming data will be runtime validated against this schema prior to being returned from the
     * fetchWithAuth helper and Typescript type will be derived from it to type the response data.
     */
    responseSchema?: TResponseSchema
    /**
     * A timeout in milliseconds for the request. If the request takes longer than this,
     * the request will be aborted, using an AbortController.
     */
    timeout?: number
  }

const useGetOptions = () => {
  const { org } = useOrg()
  const { authManager } = useAuth()

  return useCallback(
    async (_url: string, { data, ...options }: RequestOptions = {}): Promise<RequestInit> => {
      const headers = new Headers(options.headers ?? {})

      const token = await authManager?.getToken()

      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }

      if (data && !headers.get('Content-Type')) {
        headers.set('Content-Type', 'application/json')
      }

      if (org) {
        headers.set('X-Tactic-Org', org)
      }

      const bodyObject = (() => {
        if (options.body) {
          return { body: options.body }
        } else if (data) {
          return { body: JSON.stringify(data) }
        } else {
          return {}
        }
      })()

      return {
        ...options,
        ...bodyObject,
        headers,
      }
    },
    [authManager, org]
  )
}

export default useGetOptions
